/* ==========================================================================
   DB Dreamland · ATMOS engine
   Scroll-driven 3D flight, rebuilt from the way atmos.leeroy.ca works:
   - camera rides a Catmull-Rom curve, looking down its tangent
   - progress is double-smoothed (scroll lerp .2, camera lerp .025) and the
     camera brakes automatically when it reaches a chapter, so you read it
   - chapters are real 3D text planes standing in the sky along the curve
   - clouds + glider are lit by a HemisphereLight whose ground colour
     follows the sky, so cloud bellies take the colour of the moment
   - sky = sphere around the camera, vertical gradient + animated Perlin noise
   - wind streaks appear with speed, edges of the frame are blurred through a
     radial mask, a pixel grain sits on top
   - soundtrack: your .mp3, or a generative ambient score made in WebAudio,
     plus wind that follows flight speed, whoosh on take-off and UI clicks
   No 3D files, no fonts files, no audio files required. Needs three.js r149.
   ========================================================================== */
(function () {
  'use strict';

  if (window.DLA) { window.DLA.bootAll(document); return; }

  /* ---------------------------------------------------------------- utils */
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const lerp = (a, b, t) => a + (b - a) * t;
  const smooth = (t) => { t = clamp(t, 0, 1); return t * t * (3 - 2 * t); };
  const easeOut2 = (t) => 1 - (1 - t) * (1 - t);
  const easeIn2 = (t) => t * t;
  const easeInOut2 = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
  const mapLinear = (x, a1, a2, b1, b2) => b1 + ((x - a1) * (b2 - b1)) / (a2 - a1);
  const frameLerp = (k, dt) => 1 - Math.pow(1 - k, dt * 60);
  function mulberry32(a) {
    return function () {
      a |= 0; a = (a + 0x6d2b79f5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function deviceType() {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua) || (ua.includes('Mac') && 'ontouchend' in document)) return 'tablet';
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) return 'mobile';
    return 'desktop';
  }
  const safeStore = {
    get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  };

  /* ------------------------------------------------------- sky palettes
     The 8 Atmos gradients (top, bottom, noise) and the 10 ground colours of
     its HemisphereLight, plus DreamBrothers moods. A chapter set to "auto"
     follows the Atmos sequence exactly for its place in the journey.      */
  const C = (r, g, b) => [r / 255, g / 255, b / 255];
  const ATMOS_SKY = [
    [C(8, 37, 198), C(196, 203, 244), C(0, 27, 181)],
    [C(116, 136, 234), C(255, 192, 148), C(116, 136, 234)],
    [C(255, 192, 148), C(222, 229, 253), C(136, 162, 247)],
    [C(136, 162, 247), C(222, 229, 253), C(136, 162, 247)],
    [C(136, 162, 247), C(222, 229, 253), C(255, 192, 148)],
    [C(253, 175, 120), C(255, 230, 213), C(253, 175, 120)],
    [C(253, 175, 120), C(197, 211, 254), C(90, 108, 216)],
    [C(90, 108, 216), C(197, 211, 254), C(90, 108, 216)]
  ];
  const ATMOS_GROUND = [
    C(8, 37, 198), C(8, 37, 198), C(116, 136, 234), C(255, 192, 148), C(136, 162, 247),
    C(136, 162, 247), C(253, 175, 120), C(253, 175, 120), C(90, 108, 216), C(90, 108, 216)
  ];
  const WHITE = C(255, 255, 255);
  function mood(top, bottom, noise, ground, light, stars) {
    return { top, bottom, noise, ground, light: light || WHITE, stars: stars || 0 };
  }
  const MOODS = {
    atmos_1: mood(...ATMOS_SKY[0], C(8, 37, 198)),
    atmos_2: mood(...ATMOS_SKY[1], C(116, 136, 234)),
    atmos_3: mood(...ATMOS_SKY[2], C(255, 192, 148)),
    atmos_4: mood(...ATMOS_SKY[3], C(136, 162, 247)),
    atmos_5: mood(...ATMOS_SKY[4], C(136, 162, 247)),
    atmos_6: mood(...ATMOS_SKY[5], C(253, 175, 120)),
    atmos_7: mood(...ATMOS_SKY[6], C(253, 175, 120)),
    atmos_8: mood(...ATMOS_SKY[7], C(90, 108, 216)),
    dream_pink: mood(C(240, 170, 214), C(250, 222, 232), C(226, 140, 200), C(222, 128, 190)),
    dream_lavender: mood(C(176, 156, 240), C(236, 222, 250), C(150, 128, 232), C(150, 128, 232)),
    dream_morning: mood(C(120, 170, 250), C(214, 228, 255), C(96, 150, 245), C(96, 150, 245)),
    dream_day: mood(C(60, 170, 240), C(214, 248, 252), C(40, 150, 235), C(40, 150, 235)),
    storm: mood(C(70, 76, 108), C(150, 152, 178), C(52, 56, 84), C(48, 52, 80), C(196, 200, 216)),
    night: mood(C(2, 10, 32), C(16, 42, 92), C(8, 24, 64), C(10, 26, 66), C(92, 112, 168), 1)
  };
  function mixArr(out, a, b, t) { out[0] = lerp(a[0], b[0], t); out[1] = lerp(a[1], b[1], t); out[2] = lerp(a[2], b[2], t); return out; }
  function atmosMood(p) {
    const g = clamp(p / 0.95, 0, 1) * 7, i = Math.min(6, Math.floor(g)), f = g - i;
    const s = clamp(p, 0, 1) * 9, j = Math.min(8, Math.floor(s)), h = s - j;
    return {
      top: mixArr([0, 0, 0], ATMOS_SKY[i][0], ATMOS_SKY[i + 1][0], f),
      bottom: mixArr([0, 0, 0], ATMOS_SKY[i][1], ATMOS_SKY[i + 1][1], f),
      noise: mixArr([0, 0, 0], ATMOS_SKY[i][2], ATMOS_SKY[i + 1][2], f),
      ground: mixArr([0, 0, 0], ATMOS_GROUND[j], ATMOS_GROUND[j + 1], h),
      light: WHITE, stars: 0
    };
  }

  /* ----------------------------------------------------------- shaders */
  const SKY_VERT = `
    varying vec3 vPos;
    void main(){ vPos = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`;
  const SKY_FRAG = `
    uniform float uTime; uniform float uYCompression;
    uniform vec3 uTop; uniform vec3 uBottom; uniform vec3 uNoise;
    varying vec3 vPos;
    vec4 permute(vec4 x){ return mod(((x*34.0)+1.0)*x, 289.0); }
    vec2 fade(vec2 t){ return t*t*t*(t*(t*6.0-15.0)+10.0); }
    float cnoise(vec2 P){
      vec4 Pi = floor(P.xyxy) + vec4(0.0,0.0,1.0,1.0);
      vec4 Pf = fract(P.xyxy) - vec4(0.0,0.0,1.0,1.0);
      Pi = mod(Pi, 289.0);
      vec4 ix = Pi.xzxz; vec4 iy = Pi.yyww; vec4 fx = Pf.xzxz; vec4 fy = Pf.yyww;
      vec4 i = permute(permute(ix) + iy);
      vec4 gx = 2.0 * fract(i * 0.0243902439) - 1.0;
      vec4 gy = abs(gx) - 0.5; vec4 tx = floor(gx + 0.5); gx = gx - tx;
      vec2 g00 = vec2(gx.x,gy.x); vec2 g10 = vec2(gx.y,gy.y); vec2 g01 = vec2(gx.z,gy.z); vec2 g11 = vec2(gx.w,gy.w);
      vec4 norm = 1.79284291400159 - 0.85373472095314 * vec4(dot(g00,g00), dot(g01,g01), dot(g10,g10), dot(g11,g11));
      g00 *= norm.x; g01 *= norm.y; g10 *= norm.z; g11 *= norm.w;
      float n00 = dot(g00, vec2(fx.x, fy.x)); float n10 = dot(g10, vec2(fx.y, fy.y));
      float n01 = dot(g01, vec2(fx.z, fy.z)); float n11 = dot(g11, vec2(fx.w, fy.w));
      vec2 fade_xy = fade(Pf.xy);
      vec2 n_x = mix(vec2(n00, n01), vec2(n10, n11), fade_xy.x);
      return 2.3 * mix(n_x.x, n_x.y, fade_xy.y);
    }
    void main(){
      float yFactor = (.5 - (vPos.y * uYCompression) / 2.);
      float noise = cnoise(vPos.xy * .025 + uTime * .2);
      noise += cnoise(vPos.xy * .025 - uTime * .2);
      noise = clamp(noise, 0., 1.);
      vec3 vertical = mix(uTop, uBottom, yFactor);
      gl_FragColor = vec4(mix(vertical, uNoise, noise * (1. - yFactor)), 1.0);
    }`;
  const QUAD_VERT = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`;
  const COPY_FRAG = `uniform sampler2D tMap; varying vec2 vUv; void main(){ gl_FragColor = texture2D(tMap, vUv); }`;
  const BLUR_FRAG = `
    uniform sampler2D tMap; uniform vec2 uDir; varying vec2 vUv;
    void main(){
      vec4 c = texture2D(tMap, vUv) * 0.227027;
      c += texture2D(tMap, vUv + uDir * 1.3846) * 0.316216;
      c += texture2D(tMap, vUv - uDir * 1.3846) * 0.316216;
      c += texture2D(tMap, vUv + uDir * 3.2308) * 0.070270;
      c += texture2D(tMap, vUv - uDir * 3.2308) * 0.070270;
      gl_FragColor = c;
    }`;
  const FINAL_FRAG = `
    uniform sampler2D tSharp; uniform sampler2D tBlur;
    uniform float uMixerAmp; uniform float uTime; uniform float uGrain;
    uniform float uXCount; uniform float uYCount;
    varying vec2 vUv;
    float random2d(vec2 c){ return fract(sin(dot(c.xy, vec2(12.9898,78.233))) * 43758.5453); }
    void main(){
      vec4 sharp = texture2D(tSharp, vUv);
      vec4 blurred = texture2D(tBlur, vUv);
      float radial = smoothstep(.17, .97, length(vUv - .5) * 2.);
      float mixer = pow(radial, 2.);
      vec4 color = mix(sharp, blurred, mixer * uMixerAmp);
      float gridX = floor(vUv.x * uXCount) / uXCount;
      float gridY = 1.0 - floor(vUv.y * uYCount) / uYCount;
      float stepT = floor(mod(uTime, 10.) * 12.);
      float light = (color.r + color.g + color.b) / 3.;
      float grain = random2d(vec2(gridX + stepT + light / 100000., gridY)) * uGrain;
      grain -= grain / 2.;
      gl_FragColor = vec4(color.rgb + vec3(grain), 1.0);
    }`;
  const STAR_VERT = `
    attribute float aSeed; uniform float uTime; uniform float uSize; varying float vTw;
    void main(){
      vTw = 0.55 + 0.45 * sin(uTime * (0.6 + aSeed * 1.8) + aSeed * 40.0);
      vec4 mv = modelViewMatrix * vec4(position, 1.0);
      gl_PointSize = uSize * (0.6 + aSeed * 0.9);
      gl_Position = projectionMatrix * mv;
    }`;
  const STAR_FRAG = `
    uniform float uOpacity; varying float vTw;
    void main(){
      vec2 d = gl_PointCoord - .5; float a = smoothstep(.5, .0, length(d));
      gl_FragColor = vec4(vec3(1.), a * vTw * uOpacity);
    }`;

  /* ---------------------------------------------------- procedural meshes */
  // Cumulus cloud: a signed-distance field made of overlapping spheres
  // (wide base, stacked domes, small puffs on top), smooth-unioned, cut
  // flat underneath, then turned into a mesh with marching tetrahedra.
  // Normals come from the field gradient, so shading is perfectly smooth.
  function cloudField(seed) {
    const rnd = mulberry32(seed), S = [];
    const add = (x, y, z, r) => S.push([x, y, z, r]);
    const nb = 4 + Math.floor(rnd() * 2);
    for (let i = 0; i < nb; i++) add(lerp(-1.25, 1.25, i / (nb - 1)) + (rnd() - 0.5) * 0.3, -0.05 + rnd() * 0.1, (rnd() - 0.5) * 0.5, 0.5 + rnd() * 0.22);
    const nm = 3 + Math.floor(rnd() * 3);
    for (let i = 0; i < nm; i++) add((rnd() - 0.5) * 1.7, 0.32 + rnd() * 0.22, (rnd() - 0.5) * 0.45, 0.4 + rnd() * 0.2);
    const nt = 1 + Math.floor(rnd() * 3);
    for (let i = 0; i < nt; i++) add((rnd() - 0.5) * 0.9, 0.66 + rnd() * 0.2, (rnd() - 0.5) * 0.3, 0.32 + rnd() * 0.14);
    const big = S.slice();
    const np = 16 + Math.floor(rnd() * 8);
    for (let i = 0; i < np; i++) {
      const b = big[Math.floor(rnd() * big.length)];
      const th = rnd() * Math.PI * 2, y = 0.05 + rnd() * 0.95, rr = Math.sqrt(1 - y * y);
      const k = b[3] * (0.82 + rnd() * 0.12);
      add(b[0] + Math.cos(th) * rr * k, b[1] + y * k, b[2] + Math.sin(th) * rr * k * 0.8, 0.13 + rnd() * 0.15);
    }
    const ph = rnd() * 10;
    const smin = (a, b, k) => { const h = clamp(0.5 + 0.5 * (b - a) / k, 0, 1); return lerp(b, a, h) - k * h * (1 - h); };
    return (x, y, z) => {
      let d = 1e9;
      for (let i = 0; i < S.length; i++) {
        const s = S[i];
        d = smin(d, Math.hypot(x - s[0], y - s[1], z - s[2]) - s[3], s[3] < 0.3 ? 0.07 : 0.2);
      }
      d = -smin(-d, (y + 0.32), 0.12); // flat belly
      d += 0.018 * Math.sin(x * 9 + ph) * Math.sin(y * 8.3 + ph * 0.7) * Math.sin(z * 9.7 - ph);
      return d;
    };
  }
  function cloudMeshData(seed, res) {
    const f = cloudField(seed);
    const x0 = -2.3, x1 = 2.3, y0 = -0.5, y1 = 1.55, z0 = -1.25, z1 = 1.25;
    const h = (x1 - x0) / res;
    const nx = res + 1, ny = Math.ceil((y1 - y0) / h) + 1, nz = Math.ceil((z1 - z0) / h) + 1;
    const vals = new Float32Array(nx * ny * nz);
    const id = (i, j, k) => i + nx * (j + ny * k);
    for (let k = 0; k < nz; k++) for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) vals[id(i, j, k)] = f(x0 + i * h, y0 + j * h, z0 + k * h);
    const CO = [[0, 0, 0], [1, 0, 0], [1, 1, 0], [0, 1, 0], [0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1]];
    const TETS = [[0, 5, 1, 6], [0, 1, 2, 6], [0, 2, 3, 6], [0, 3, 7, 6], [0, 7, 4, 6], [0, 4, 5, 6]];
    const pos = [];
    const cache = new Map();
    const pv = new Array(8), vv = new Array(8), gi = new Array(8);
    const edge = (a, b) => {
      const key = gi[a] < gi[b] ? gi[a] * 7919 + gi[b] : gi[b] * 7919 + gi[a];
      let p = cache.get(key);
      if (!p) {
        const t = vv[a] / (vv[a] - vv[b]);
        p = [lerp(pv[a][0], pv[b][0], t), lerp(pv[a][1], pv[b][1], t), lerp(pv[a][2], pv[b][2], t)];
        cache.set(key, p);
      }
      return p;
    };
    const tri = (a, b, c) => pos.push(a, b, c);
    for (let k = 0; k < nz - 1; k++) for (let j = 0; j < ny - 1; j++) for (let i = 0; i < nx - 1; i++) {
      let anyIn = false, anyOut = false;
      for (let c = 0; c < 8; c++) {
        const ii = i + CO[c][0], jj = j + CO[c][1], kk = k + CO[c][2];
        gi[c] = id(ii, jj, kk); vv[c] = vals[gi[c]];
        pv[c] = [x0 + ii * h, y0 + jj * h, z0 + kk * h];
        if (vv[c] < 0) anyIn = true; else anyOut = true;
      }
      if (!anyIn || !anyOut) continue;
      for (const T of TETS) {
        const ins = T.filter((c) => vv[c] < 0), outs = T.filter((c) => vv[c] >= 0);
        if (ins.length === 0 || ins.length === 4) continue;
        if (ins.length === 1 || ins.length === 3) {
          const o = ins.length === 1 ? ins[0] : outs[0], r = ins.length === 1 ? outs : ins;
          tri(edge(o, r[0]), edge(o, r[1]), edge(o, r[2]));
        } else {
          const a = edge(ins[0], outs[0]), b = edge(ins[0], outs[1]), c = edge(ins[1], outs[1]), d = edge(ins[1], outs[0]);
          tri(a, b, c); tri(a, c, d);
        }
      }
    }
    const n = pos.length, P = new Float32Array(n * 3), N = new Float32Array(n * 3), e = h * 0.5;
    const nrm = new Map();
    const normalOf = (p) => {
      let v = nrm.get(p);
      if (!v) {
        const gx = f(p[0] + e, p[1], p[2]) - f(p[0] - e, p[1], p[2]);
        const gy = f(p[0], p[1] + e, p[2]) - f(p[0], p[1] - e, p[2]);
        const gz = f(p[0], p[1], p[2] + e) - f(p[0], p[1], p[2] - e);
        const l = Math.hypot(gx, gy, gz) || 1; v = [gx / l, gy / l, gz / l]; nrm.set(p, v);
      }
      return v;
    };
    for (let t = 0; t < n; t += 3) {
      let a = pos[t], b = pos[t + 1], c = pos[t + 2];
      const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2], wx = c[0] - a[0], wy = c[1] - a[1], wz = c[2] - a[2];
      const fx = uy * wz - uz * wy, fy = uz * wx - ux * wz, fz = ux * wy - uy * wx;
      const na = normalOf(a);
      if (fx * na[0] + fy * na[1] + fz * na[2] < 0) { const s = b; b = c; c = s; }
      [a, b, c].forEach((p, q) => {
        const o = (t + q) * 3, nn = normalOf(p);
        P[o] = p[0]; P[o + 1] = p[1]; P[o + 2] = p[2]; N[o] = nn[0]; N[o + 1] = nn[1]; N[o + 2] = nn[2];
      });
    }
    return { P, N };
  }
  function cloudGeometry(P, N) {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(P, 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(N, 3));
    geo.computeBoundingSphere();
    return geo;
  }
  // The cloud meshes are computed in a Web Worker so the page never freezes
  // (on a phone this is ~1.5 s of maths). Falls back to idle-time chunks.
  function cloudWorkerSource() {
    return 'const clamp=' + clamp + ';const lerp=' + lerp + ';' + mulberry32 + ';' + cloudField + ';' + cloudMeshData + ';' +
      'onmessage=function(e){var t0=Date.now();var d=cloudMeshData(e.data.seed,e.data.res);' +
      'postMessage({k:e.data.k,P:d.P,N:d.N,ms:Date.now()-t0},[d.P.buffer,d.N.buffer]);};';
  }

  // Sailplane: lathe fuselage + canopy, one-piece wing with dihedral,
  // tailplane and fin. Nose points +Z like the Atmos "planeur".
  function makeGlider(material) {
    const group = new THREE.Group();
    const add = (geo) => { const m = new THREE.Mesh(geo, material); group.add(m); return m; };

    const prof = [[0, 0.66], [0.035, 0.64], [0.07, 0.59], [0.098, 0.5], [0.114, 0.38], [0.118, 0.22], [0.11, 0.04], [0.088, -0.22], [0.06, -0.55], [0.04, -0.88], [0.028, -1.06], [0.014, -1.12], [0, -1.125]];
    const fus = new THREE.LatheGeometry(prof.map((p) => new THREE.Vector2(p[0], p[1])), 28);
    fus.rotateX(Math.PI / 2); fus.scale(1, 0.86, 1);
    add(fus);

    const canopy = new THREE.SphereGeometry(1, 28, 16);
    canopy.scale(0.075, 0.06, 0.24); canopy.translate(0, 0.075, 0.34);
    add(canopy);

    function planform(span, rootLE, rootTE, tipLE, tipTE, tipRound) {
      const s = new THREE.Shape();
      s.moveTo(-span + tipRound, tipTE);
      s.lineTo(0, rootTE);
      s.lineTo(span - tipRound, tipTE);
      s.quadraticCurveTo(span, (tipTE + tipLE) / 2, span - tipRound, tipLE);
      s.lineTo(0, rootLE);
      s.lineTo(-span + tipRound, tipLE);
      s.quadraticCurveTo(-span, (tipTE + tipLE) / 2, -span + tipRound, tipTE);
      return s;
    }
    function surface(shape, depth, bevel, dihedral) {
      const g = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: true, bevelThickness: bevel, bevelSize: bevel, bevelSegments: 4, curveSegments: 18, steps: 1 });
      g.rotateX(Math.PI / 2); g.translate(0, depth / 2, 0);
      const p = g.attributes.position;
      for (let i = 0; i < p.count; i++) {
        const x = p.getX(i);
        p.setY(i, p.getY(i) + Math.abs(x) * dihedral - Math.pow(Math.abs(x), 2) * 0.01);
      }
      g.computeVertexNormals();
      return g;
    }
    const wing = surface(planform(1.39, 0.2, -0.17, 0.11, -0.04, 0.09), 0.022, 0.012, 0.07);
    wing.translate(0, 0.075, 0.06);
    add(wing);

    const tail = surface(planform(0.46, -0.86, -1.06, -0.93, -1.04, 0.05), 0.014, 0.008, 0.02);
    tail.translate(0, 0.035, 0);
    add(tail);

    const fs = new THREE.Shape();
    fs.moveTo(-0.84, 0); fs.lineTo(-1.1, 0); fs.lineTo(-1.13, 0.3);
    fs.quadraticCurveTo(-1.11, 0.33, -1.06, 0.32); fs.lineTo(-0.98, 0.27); fs.lineTo(-0.84, 0);
    const fin = new THREE.ExtrudeGeometry(fs, { depth: 0.014, bevelEnabled: true, bevelThickness: 0.007, bevelSize: 0.007, bevelSegments: 3, curveSegments: 12 });
    fin.rotateY(-Math.PI / 2); fin.translate(0.007, 0.02, 0);
    fin.computeVertexNormals();
    add(fin);
    return group;
  }

  // Folded paper dart (brand option). Flat-shaded, double-sided.
  function makePaperPlane(material) {
    const N = [0, 0, 0.95], T = [0, 0.02, -0.75], K = [0, -0.2, -0.72];
    const L = [-0.82, 0.1, -0.82], R = [0.82, 0.1, -0.82];
    const LI = [-0.1, 0.0, -0.76], RI = [0.1, 0.0, -0.76];
    const tris = [N, L, LI, N, LI, T, N, RI, R, N, T, RI, N, T, K, N, K, T];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(tris.flat(), 3));
    geo.computeVertexNormals();
    const mat = material.clone(); mat.side = THREE.DoubleSide; mat.flatShading = true;
    const g = new THREE.Group(); const m = new THREE.Mesh(geo, mat); m.scale.setScalar(1.25); g.add(m);
    g.userData.materials = [mat];
    return g;
  }

  /* ------------------------------------------------------- canvas text */
  function wrapLines(ctx, text, maxW) {
    const out = [];
    String(text).split(/\n/).forEach((para) => {
      const words = para.split(/\s+/).filter(Boolean); let line = '';
      words.forEach((w) => {
        const test = line ? line + ' ' + w : w;
        if (ctx.measureText(test).width > maxW && line) { out.push(line); line = w; } else line = test;
      });
      out.push(line);
    });
    return out;
  }

  // Same number of lines, but as even as possible (no orphan words).
  function balancedWrap(ctx, text, maxW) {
    const paras = String(text).split(/\n/);
    if (paras.length > 1) return paras.reduce((out, p) => out.concat(balancedWrap(ctx, p, maxW)), []);
    const base = wrapLines(ctx, text, maxW);
    if (base.length < 2) return base;
    let lo = maxW * 0.45, hi = maxW, best = base;
    for (let k = 0; k < 12; k++) {
      const mid = (lo + hi) / 2, l = wrapLines(ctx, text, mid);
      if (l.length <= base.length) { best = l; hi = mid; } else lo = mid;
    }
    return best;
  }

  /* ------------------------------------------------------------- audio
     The built-in "Cloud Drift" score is pre-rendered (assets/db-cloud-drift.mp3:
     a 150 s loop + 8 s tail). Two <audio> copies take turns: the next copy
     starts exactly at the loop point while the previous one rings out its
     tail, so the join is seamless and costs almost no CPU. Only the wind,
     the night/day tone filter and the UI sounds are live WebAudio. */
  class Sound {
    constructor(opts) {
      this.custom = opts.url || '';
      this.src = this.custom || (opts.generative !== false ? opts.builtin || '' : '');
      this.loopAt = this.custom ? 0 : opts.loopAt || 0;
      this.volume = opts.volume;
      this.storeKey = 'dla-mute';
      this.on = false;
      this.ctx = null;
      this.night = 0;
      this.els = [];
    }
    get muted() { return safeStore.get(this.storeKey) === 'true'; }
    hasMusic() { return !!this.src; }
    prepare() {
      if (this.ctx || !this.src || this.muted) return;
      this.ensureCtx();
      if (this.ctx && !this.els.length) { this.els = this.loopAt ? [this.makeEl(), this.makeEl()] : [this.makeEl()]; this.cur = 0; }
    }
    ensureCtx() {
      if (this.ctx) return this.ctx;
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      const ctx = (this.ctx = new AC());
      this.master = ctx.createGain(); this.master.gain.value = 0;
      this.master.connect(ctx.destination);
      this.fx = ctx.createGain(); this.fx.gain.value = 0.9; this.fx.connect(ctx.destination);
      this.tone = ctx.createBiquadFilter(); this.tone.type = 'lowpass'; this.tone.frequency.value = 12000; this.tone.Q.value = 0.2;
      this.music = ctx.createGain(); this.music.gain.value = 1;
      this.music.connect(this.tone); this.tone.connect(this.master);
      // wind: one looping noise buffer through a band-pass that follows speed
      const len = ctx.sampleRate, buf = ctx.createBuffer(1, len, ctx.sampleRate), d = buf.getChannelData(0);
      let b0 = 0, b1 = 0, b2 = 0;
      for (let i = 0; i < len; i++) {
        const w = Math.random() * 2 - 1;
        b0 = 0.99765 * b0 + w * 0.099046; b1 = 0.963 * b1 + w * 0.2965164; b2 = 0.57 * b2 + w * 1.0526913;
        d[i] = (b0 + b1 + b2 + w * 0.1848) * 0.18;
      }
      this.noiseBuf = buf;
      const src = ctx.createBufferSource(); src.buffer = buf; src.loop = true;
      this.windFilter = ctx.createBiquadFilter(); this.windFilter.type = 'bandpass'; this.windFilter.frequency.value = 420; this.windFilter.Q.value = 0.7;
      this.windGain = ctx.createGain(); this.windGain.gain.value = 0;
      src.connect(this.windFilter); this.windFilter.connect(this.windGain); this.windGain.connect(this.master);
      src.start();
      return ctx;
    }
    // CORS load so WebAudio can filter it (Shopify's CDN allows it); a track
    // hosted without CORS falls back to plain playback (no night filter)
    makeEl(plain) {
      const a = new Audio();
      if (!plain) a.crossOrigin = 'anonymous';
      a.preload = 'auto'; a.loop = !this.loopAt; a.setAttribute('playsinline', ''); a.src = this.src;
      if (!plain) {
        try { this.ctx.createMediaElementSource(a).connect(this.music); } catch (e) { /* already routed */ }
        a.addEventListener('error', () => {
          const i = this.els.indexOf(a); if (i < 0) return;
          const b = this.makeEl(true); this.els[i] = b;
          if (this.on && i === this.cur) { const q = b.play(); if (q && q.catch) q.catch(() => {}); }
        }, { once: true });
      } else a.volume = clamp(this.volume * 2, 0, 1);
      return a;
    }
    startMusic() {
      if (!this.src || !this.ctx) return;
      if (!this.els.length) { this.els = this.loopAt ? [this.makeEl(), this.makeEl()] : [this.makeEl()]; this.cur = 0; }
      const a = this.els[this.cur];
      const p = a.play(); if (p && p.catch) p.catch(() => {});
      if (this.loopAt && !this.loopTimer) {
        // hand over to the other copy at the loop point (timer + exact timeout)
        this.loopTimer = setInterval(() => {
          const now = this.els[this.cur];
          if (!this.on || now.paused || this.handing) return;
          const left = this.loopAt - now.currentTime;
          if (left < 0.25) {
            this.handing = true;
            setTimeout(() => {
              const next = this.els[1 - this.cur];
              try { next.currentTime = 0; } catch (e) { /* not loaded yet */ }
              const q = next.play(); if (q && q.catch) q.catch(() => {});
              this.cur = 1 - this.cur; this.handing = false;
            }, Math.max(0, left * 1000));
          }
        }, 60);
      }
    }
    pauseMusic() { this.els.forEach((a) => a.pause()); }
    start() {
      if (this.muted || !this.src) return false;
      const ctx = this.ensureCtx();
      if (!ctx) return false;
      if (ctx.state === 'suspended') ctx.resume();
      this.master.gain.cancelScheduledValues(ctx.currentTime);
      this.master.gain.setTargetAtTime(this.volume * 2.4, ctx.currentTime, 0.6);
      this.startMusic();
      this.on = true;
      return true;
    }
    stop() {
      if (this.ctx) this.master.gain.setTargetAtTime(0, this.ctx.currentTime, 0.35);
      clearTimeout(this.pauseT); this.pauseT = setTimeout(() => { if (!this.on) this.pauseMusic(); }, 1600);
      this.on = false;
    }
    toggle() {
      if (this.on) { safeStore.set(this.storeKey, 'true'); this.stop(); return false; }
      safeStore.set(this.storeKey, 'false'); return this.start();
    }
    update(speed, brightness, info) {
      if (!this.ctx || !this.on) return;
      const t = this.ctx.currentTime, s = clamp(speed, 0, 1);
      if (info) this.night = lerp(this.night, info.night, 0.02);
      this.windGain.gain.setTargetAtTime(0.03 + s * 0.4, t, 0.25);
      this.windFilter.frequency.setTargetAtTime(380 + s * 1500, t, 0.3);
      // darker, softer at night; open in daylight
      this.tone.frequency.setTargetAtTime(2200 + 9000 * (1 - this.night) * clamp(brightness + 0.2, 0, 1), t, 1.2);
      this.music.gain.setTargetAtTime(1 - this.night * 0.18, t, 1.2);
    }
    whoosh(strength) {
      if (!this.on || !this.ctx) return;
      const ctx = this.ctx, t = ctx.currentTime, d = 1.9;
      const src = ctx.createBufferSource(); src.buffer = this.noiseBuf;
      const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 1.1;
      bp.frequency.setValueAtTime(260, t); bp.frequency.exponentialRampToValueAtTime(2400, t + d * 0.45); bp.frequency.exponentialRampToValueAtTime(500, t + d);
      const g = ctx.createGain(); g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.8 * (strength || 1), t + d * 0.42); g.gain.exponentialRampToValueAtTime(0.0001, t + d);
      src.connect(bp); bp.connect(g); g.connect(this.fx);
      src.loop = true; src.start(t, Math.random() * 0.9); src.stop(t + d + 0.1);
    }
    click() {
      if (!this.on || !this.ctx) return;
      const ctx = this.ctx, t = ctx.currentTime;
      const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.setValueAtTime(1180, t); o.frequency.exponentialRampToValueAtTime(620, t + 0.09);
      const g = ctx.createGain(); g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.1, t + 0.004); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
      o.connect(g); g.connect(this.fx); o.start(t); o.stop(t + 0.16);
    }
    suspend(hidden) {
      if (!this.on) return;
      if (hidden) { this.pauseMusic(); if (this.ctx) this.ctx.suspend(); }
      else { if (this.ctx) this.ctx.resume(); this.startMusic(); }
    }
    destroy() {
      clearInterval(this.loopTimer); clearTimeout(this.pauseT);
      this.els.forEach((a) => { a.pause(); a.removeAttribute('src'); a.load(); });
      this.els = [];
      if (this.ctx && this.ctx.close) { try { this.ctx.close(); } catch (e) { /* noop */ } }
      this.ctx = null;
    }
  }

  /* ---------------------------------------------------- post processing */
  class Post {
    constructor(renderer, webgl2) {
      this.renderer = renderer;
      const opts = { depthBuffer: true, stencilBuffer: false };
      this.rtMain = new THREE.WebGLRenderTarget(4, 4, opts);
      if (webgl2) this.rtMain.samples = 4;
      this.rtA = new THREE.WebGLRenderTarget(4, 4, { depthBuffer: false });
      this.rtB = new THREE.WebGLRenderTarget(4, 4, { depthBuffer: false });
      this.cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
      this.scene = new THREE.Scene();
      this.quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2));
      this.quad.frustumCulled = false;
      this.scene.add(this.quad);
      this.copyMat = new THREE.ShaderMaterial({ vertexShader: QUAD_VERT, fragmentShader: COPY_FRAG, uniforms: { tMap: { value: null } }, depthTest: false, depthWrite: false });
      this.blurMat = new THREE.ShaderMaterial({ vertexShader: QUAD_VERT, fragmentShader: BLUR_FRAG, uniforms: { tMap: { value: null }, uDir: { value: new THREE.Vector2() } }, depthTest: false, depthWrite: false });
      this.finalMat = new THREE.ShaderMaterial({
        vertexShader: QUAD_VERT, fragmentShader: FINAL_FRAG, depthTest: false, depthWrite: false,
        uniforms: { tSharp: { value: null }, tBlur: { value: null }, uMixerAmp: { value: 1 }, uTime: { value: 0 }, uGrain: { value: 0.065 }, uXCount: { value: 100 }, uYCount: { value: 100 } }
      });
    }
    setSize(w, h, dpr) {
      const W = Math.max(2, Math.floor(w * dpr)), H = Math.max(2, Math.floor(h * dpr));
      this.rtMain.setSize(W, H);
      const div = 2;
      this.bw = Math.max(2, Math.floor(W / div)); this.bh = Math.max(2, Math.floor(H / div));
      this.rtA.setSize(this.bw, this.bh); this.rtB.setSize(this.bw, this.bh);
      this.finalMat.uniforms.uXCount.value = W / dpr; this.finalMat.uniforms.uYCount.value = H / dpr;
    }
    pass(mat, target) { this.quad.material = mat; this.renderer.setRenderTarget(target); this.renderer.render(this.scene, this.cam); }
    render(scene, camera, mixerAmp, time, blurOn) {
      const r = this.renderer;
      r.setRenderTarget(this.rtMain); r.render(scene, camera);
      if (blurOn) {
        this.copyMat.uniforms.tMap.value = this.rtMain.texture; this.pass(this.copyMat, this.rtA);
        for (let i = 1; i <= (this.lite ? 1 : 2); i++) {
          this.blurMat.uniforms.tMap.value = this.rtA.texture; this.blurMat.uniforms.uDir.value.set(i / this.bw, 0); this.pass(this.blurMat, this.rtB);
          this.blurMat.uniforms.tMap.value = this.rtB.texture; this.blurMat.uniforms.uDir.value.set(0, i / this.bh); this.pass(this.blurMat, this.rtA);
        }
      }
      const u = this.finalMat.uniforms;
      u.tSharp.value = this.rtMain.texture; u.tBlur.value = blurOn ? this.rtA.texture : this.rtMain.texture;
      u.uMixerAmp.value = blurOn ? mixerAmp : 0; u.uTime.value = time;
      this.pass(this.finalMat, null);
    }
    dispose() {
      [this.rtMain, this.rtA, this.rtB].forEach((t) => t.dispose());
      [this.copyMat, this.blurMat, this.finalMat].forEach((m) => m.dispose());
      this.quad.geometry.dispose();
    }
  }

  /* ========================================================= experience */
  class Dreamland {
    constructor(root) {
      this.root = root;
      this.alive = true;
      this.listeners = [];
      try {
        this.data = JSON.parse(root.querySelector('.dla-data').textContent);
      } catch (e) { console.error('[Dreamland] bad data', e); return; }
      this.s = this.data.settings;
      this.chapters = (this.data.chapters || []).map((c) => Object.assign({}, c, {
        body: Array.isArray(c.body) ? c.body.filter((l) => l && String(l).trim()).join('\n') : c.body || ''
      }));
      this.N = this.chapters.length;
      this.q = (sel) => root.querySelector(sel);
      this.els = {
        track: this.q('.dla-track'), stage: this.q('.dla-stage'), canvas: this.q('.dla-canvas'),
        intro: this.q('.dla-intro'), title: this.q('.dla-intro__title'), cta: this.q('.dla-intro__cta'),
        hint: this.q('.dla-hint'), sound: this.q('.dla-sound'), topLink: this.q('.dla-toplink'),
        rail: this.q('.dla-rail'), handle: this.q('.dla-rail__handle'), counter: this.q('.dla-counter'),
        outro: this.q('.dla-outro'), replay: this.q('[data-dla-replay]')
      };
      this.device = deviceType();
      this.isTouch = this.device !== 'desktop';
      this.designMode = !!(window.Shopify && window.Shopify.designMode);
      this.layoutTrack();
      this.splitTitle();
      this.sound = new Sound({ url: this.s.audio_url, generative: this.s.generative_music, builtin: this.s.builtin_audio, loopAt: this.s.builtin_loop, volume: this.s.music_volume });
      if (!this.sound.hasMusic()) root.classList.add('dla--silent');
      if (this.sound.muted) root.classList.add('dla--muted');
      this.bindUI();
      if (this.s.show_counter) root.classList.add('dla--show-counter');
      root.style.setProperty('--dla-handle', clamp(100 / (this.N + 2), 6, 20).toFixed(1) + '%');
      if (!this.initGL()) { root.classList.add('dla--nogl'); return; }
      this.buildScene();
      this.state = { started: false, ended: false, am: 0, tgt: 0, introT: 0, cloudAlpha: 0, lineAlpha: 0, endT: 0, t: 0 };
      this.root.classList.add('dla--ready');
      requestAnimationFrame(() => root.classList.add('dla--intro-in'));
      // after the intro has painted, warm up the audio graph in idle time
      setTimeout(() => (window.requestIdleCallback || ((cb) => setTimeout(cb, 50)))(() => { if (this.alive) this.sound.prepare(); }, { timeout: 2000 }), 900);
      this.last = performance.now();
      this.loop = this.loop.bind(this);
      this.raf = requestAnimationFrame(this.loop);
    }

    on(target, type, fn, opts) { target.addEventListener(type, fn, opts); this.listeners.push([target, type, fn, opts]); }

    /* ---------- layout: scroll lengths -> track height + chapter progress */
    layoutTrack() {
      const s = this.s, N = Math.max(1, this.N);
      this.totalVh = s.intro_length + N * s.chapter_length + s.outro_length;
      this.els.track.style.setProperty('--dla-total', this.totalVh);
      const run = s.intro_length + N * s.chapter_length;
      this.rEnd = run / this.totalVh;
      this.chapterP = this.chapters.map((c, i) => 0.95 * (s.intro_length + (i + 0.5) * s.chapter_length) / run);
    }

    splitTitle() {
      const h = this.els.title; if (!h || h.querySelector('img')) return;
      const txt = h.textContent.trim(); h.textContent = '';
      h.style.setProperty('--dla-len', Math.max(4, txt.length));
      const rnd = mulberry32(txt.length * 31 + 7);
      [...txt].forEach((ch) => {
        const sp = document.createElement('span');
        sp.textContent = ch === ' ' ? ' ' : ch;
        sp.style.transitionDelay = (rnd() * 0.5).toFixed(2) + 's';
        h.appendChild(sp);
      });
      h.setAttribute('aria-label', txt);
    }

    bindUI() {
      const { cta, sound, replay, topLink } = this.els;
      if (cta) this.on(cta, 'click', () => this.start(true));
      const hint = this.q('.dla-soundhint');
      if (hint) this.on(hint, 'click', () => this.enableSound(true));
      // flight started by scrolling = silent; the first tap/click/key anywhere turns the sound on
      const unlock = (e) => {
        if (!this.state || !this.state.started || !this.inView || this.sound.on || this.sound.muted) return;
        if (e.target && e.target.closest && e.target.closest('.dla-sound, .dla-intro__cta, .dla-soundhint')) return;
        this.enableSound(false);
      };
      ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown'].forEach((t) => this.on(document, t, unlock, { passive: true }));
      if (sound) this.on(sound, 'click', () => {
        const playing = this.sound.toggle();
        this.root.classList.toggle('dla--playing', !!playing);
        this.root.classList.toggle('dla--muted', !playing);
        sound.setAttribute('aria-pressed', playing ? 'true' : 'false');
        if (playing) this.sound.click();
      });
      if (replay) this.on(replay, 'click', () => this.replay());
      if (topLink) this.on(topLink, 'click', () => this.sound.click());
      this.root.querySelectorAll('.dla-btn').forEach((b) => this.on(b, 'click', () => this.sound.click()));
      this.on(document, 'visibilitychange', () => this.sound.suspend(document.visibilityState !== 'visible'));
    }

    initGL() {
      try {
        const canvas = this.els.canvas;
        this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false, depth: true, stencil: false, powerPreference: 'high-performance', preserveDrawingBuffer: !!window.DLA_PRESERVE });
      } catch (e) { return false; }
      if (!this.renderer || !this.renderer.getContext()) return false;
      this.webgl2 = this.renderer.capabilities.isWebGL2;
      this.renderer.autoClear = true;
      // phones: cap at 1.5x (sharp enough on a 6" screen, ~45% fewer pixels than 2x)
      // like Atmos: up to 2x on phones; never below 1.25x (that looked pixelated)
      this.dpr = this.device === 'desktop' ? Math.min(window.devicePixelRatio || 1, 1.5) : Math.min(window.devicePixelRatio || 1, 2);
      this.minDpr = Math.min(this.dpr, this.device === 'desktop' ? 1 : 1.25);
      this.post = new Post(this.renderer, this.webgl2 && this.device === 'desktop');
      this.post.lite = this.device !== 'desktop';
      this.blurOn = this.s.edge_blur;
      return true;
    }

    /* ---------------------------------------------------------- scene */
    buildScene() {
      const s = this.s;
      const scene = (this.scene = new THREE.Scene());
      const camera = (this.camera = new THREE.PerspectiveCamera(60, 1, 0.1, 140));
      scene.add(camera);

      this.light = new THREE.HemisphereLight(0xffffff, 0x0000ff, 1);
      this.light.position.set(-10, 10, 0);
      scene.add(this.light);

      // flight path: 0,0 then S-curves, scaled so chapters stay ~30u apart
      const N = Math.max(1, this.N);
      const length = Math.max(170, 300 * (N + 1) / 10);
      const segs = Math.max(4, Math.round(length / 50));
      const step = length / segs;
      const sign = Math.random() > 0.5 ? 1 : -1;
      const pts = [];
      for (let i = 0; i <= segs; i++) {
        const inner = i >= 2 && i <= segs - 2;
        const x = inner ? 12.5 * sign * (i % 2 === 0 ? 1 : -1) : 0;
        pts.push(new THREE.Vector3(x, 0, -i * step));
      }
      this.curve = new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.5);
      this.L = this.curve.getLength();
      const U = (units) => units / this.L;
      this.ahead = { wind: U(1.2), plane: U(6), look: U(12), pre: U(5.6), textLook: U(7.6), cloudLook: U(38) };
      this.holdRadius = 12.5;

      this.lineMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, depthWrite: false });
      this.line = new THREE.Mesh(new THREE.TubeGeometry(this.curve, Math.round(length * 1.6), 0.02, 5, false), this.lineMat);
      this.line.position.y = -0.5;
      this.line.visible = s.show_path;
      scene.add(this.line);

      // sky dome
      this.skyMat = new THREE.ShaderMaterial({
        vertexShader: SKY_VERT, fragmentShader: SKY_FRAG, side: THREE.BackSide, depthWrite: false,
        uniforms: { uTime: { value: 0 }, uYCompression: { value: 0.06 }, uTop: { value: new THREE.Color() }, uBottom: { value: new THREE.Color() }, uNoise: { value: new THREE.Color() } }
      });
      this.sky = new THREE.Mesh(new THREE.SphereGeometry(50, 32, 16), this.skyMat);
      this.sky.renderOrder = -10;
      scene.add(this.sky);

      // stars (only seen in night moods)
      {
        const n = this.device === 'desktop' ? 900 : 380, p = new Float32Array(n * 3), seed = new Float32Array(n), rnd = mulberry32(99);
        for (let i = 0; i < n; i++) {
          const u = rnd() * 2 - 1, th = rnd() * Math.PI * 2, r = Math.sqrt(1 - u * u);
          const y = Math.abs(u) * 0.9 + 0.05;
          p[i * 3] = Math.cos(th) * r * 44; p[i * 3 + 1] = (y * 2 - 0.6) * 30; p[i * 3 + 2] = Math.sin(th) * r * 44;
          seed[i] = rnd();
        }
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(p, 3)); g.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
        this.starMat = new THREE.ShaderMaterial({ vertexShader: STAR_VERT, fragmentShader: STAR_FRAG, transparent: true, depthWrite: false, uniforms: { uTime: { value: 0 }, uOpacity: { value: 0 }, uSize: { value: 2.4 * this.dpr } } });
        this.stars = new THREE.Points(g, this.starMat); this.stars.renderOrder = -9; this.stars.frustumCulled = false;
        scene.add(this.stars);
      }

      this.buildMoodKeys();
      this.buildPlane();
      this.buildClouds();
      this.buildTexts();
      this.buildWind();
      this.resize();
      this.precompile();
      if (window.ResizeObserver) { this.ro = new ResizeObserver(() => this.resize()); this.ro.observe(this.els.stage); }
      this.on(window, 'resize', () => this.resize());
      this.on(window, 'scroll', () => { this.sy = window.scrollY; this.guardEnd(); }, { passive: true });
      this.on(window, 'load', () => { this.trackTop = null; });
      if (window.ResizeObserver) { this.bodyRo = new ResizeObserver(() => { this.trackTop = null; }); this.bodyRo.observe(document.body); }
      if (window.IntersectionObserver) {
        this.inView = true;
        this.io = new IntersectionObserver((e) => { this.inView = e[0].isIntersecting; }, { rootMargin: '200px' });
        this.io.observe(this.els.track);
        // while the stage covers most of the screen, other animations on the
        // page (footer waves, announcement gradient, badges...) are hidden
        // behind it but kept forcing layout + full-page repaints every frame
        this.io2 = new IntersectionObserver((e) => {
          const on = e[0].intersectionRatio > 0.6;
          document.documentElement.classList.toggle('dla-focus', on);
        }, { threshold: [0, 0.6, 1] });
        this.io2.observe(this.els.stage);
      } else this.inView = true;
    }

    // Compile all GPU programs now, while the intro is on screen, instead of
    // on the first frame where clouds/text appear (that was a visible stall).
    precompile() {
      const hidden = [];
      this.scene.traverse((o) => { if (!o.visible) { hidden.push(o); o.visible = true; } });
      const tex = new THREE.CanvasTexture(document.createElement('canvas'));
      const dummy = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false }));
      this.scene.add(dummy);
      try { this.renderer.compile(this.scene, this.camera); } catch (e) { /* compile lazily */ }
      this.scene.remove(dummy); dummy.geometry.dispose(); dummy.material.dispose(); tex.dispose();
      hidden.forEach((o) => { o.visible = false; });
    }

    buildMoodKeys() {
      const keys = [{ p: 0, mood: this.s.intro_sky }];
      this.chapters.forEach((c, i) => keys.push({ p: this.chapterP[i], mood: c.dark ? 'night' : c.sky }));
      keys.push({ p: 0.95, mood: this.s.outro_sky }, { p: 1.01, mood: this.s.outro_sky });
      this.moodKeys = keys;
      this.cur = { top: [0, 0, 0], bottom: [0, 0, 0], noise: [0, 0, 0], ground: [0, 0, 0], light: [1, 1, 1], stars: 0 };
    }
    moodAt(key, p) { return !key.mood || key.mood === 'auto' || !MOODS[key.mood] ? atmosMood(p) : MOODS[key.mood]; }
    sampleMood(p, c) {
      const k = this.moodKeys; let j = 0;
      while (j < k.length - 2 && p > k[j + 1].p) j++;
      const a = k[j], b = k[j + 1];
      const t = smooth((p - a.p) / Math.max(1e-5, b.p - a.p));
      const A = this.moodAt(a, p), B = this.moodAt(b, p);
      ['top', 'bottom', 'noise', 'ground', 'light'].forEach((n) => mixArr(c[n], A[n], B[n], t));
      c.stars = lerp(A.stars, B.stars, t);
      return c;
    }
    updateMood(p) {
      const c = this.sampleMood(p, this.cur);
      const u = this.skyMat.uniforms;
      u.uTop.value.fromArray(c.top); u.uBottom.value.fromArray(c.bottom); u.uNoise.value.fromArray(c.noise);
      this.light.groundColor.fromArray(c.ground);
      this.light.color.fromArray(c.light);
      this.starMat.uniforms.uOpacity.value = c.stars;
      this.stars.visible = c.stars > 0.01;
      this.brightness = clamp((c.top[0] + c.top[1] + c.top[2] + c.bottom[0] + c.bottom[1] + c.bottom[2]) / 4.2, 0, 1);
    }

    buildPlane() {
      const pc = new THREE.Color(this.s.plane_color || '#ffffff');
      this.planeMat = new THREE.MeshLambertMaterial({ color: pc, emissive: pc.clone().multiplyScalar(0.32), transparent: true });
      this.plane = this.s.plane_model === 'paper' ? makePaperPlane(this.planeMat) : makeGlider(this.planeMat);
      this.planeMats = this.plane.userData.materials || [this.planeMat];
      this.plane.scale.setScalar(this.s.plane_scale || 1);
      this.scene.add(this.plane);
      this.planeBase = new THREE.Vector3(); this.planeLook = new THREE.Vector3();
      this.introOffset = new THREE.Vector3(0, -2, 17.5);
    }

    buildClouds() {
      const res = this.device === 'desktop' ? 56 : 34;
      // geometry is generated after the first frames (idle time), one cloud
      // shape at a time, so the intro shows instantly; clouds are hidden
      // until take-off anyway
      this.cloudGeos = [new THREE.BufferGeometry(), new THREE.BufferGeometry(), new THREE.BufferGeometry()];
      this.cloudGenMs = 0;
      const seeds = [11, 22, 37];
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 16));
      const apply = (k, P, N) => {
        if (!this.alive) return;
        const g = cloudGeometry(P, N), old = this.cloudGeos[k]; this.cloudGeos[k] = g;
        this.clouds.forEach((m) => { if (m.userData.shape === k) m.geometry = g; });
        old.dispose();
        this.cloudsReady = (this.cloudsReady || 0) + 1;
      };
      const genNext = (k) => { // main-thread fallback
        if (!this.alive || k > 2) return;
        const t0 = performance.now(); const d = cloudMeshData(seeds[k], res);
        this.cloudGenMs += Math.round(performance.now() - t0);
        apply(k, d.P, d.N);
        idle(() => genNext(k + 1), { timeout: 400 });
      };
      const fallback = () => { this.cloudWorkerFailed = true; setTimeout(() => idle(() => genNext(0), { timeout: 400 }), 120); };
      try {
        const url = URL.createObjectURL(new Blob([cloudWorkerSource()], { type: 'text/javascript' }));
        const w = (this.cloudWorker = new Worker(url)); let got = 0;
        w.onmessage = (e) => { this.cloudGenMs += e.data.ms; apply(e.data.k, e.data.P, e.data.N); if (++got === 3) { w.terminate(); URL.revokeObjectURL(url); this.cloudWorker = null; } };
        w.onerror = (e) => { if (e && e.preventDefault) e.preventDefault(); w.terminate(); this.cloudWorker = null; if (!got) fallback(); };
        seeds.forEach((seed, k) => w.postMessage({ k, seed, res }));
      } catch (e) { fallback(); }
      const rnd = mulberry32(2026 + this.N);
      const P = this.curve, tmp = new THREE.Vector3(), look = new THREE.Vector3();
      const nearestChapter = (p) => {
        let best = -1, d = 1;
        this.chapterP.forEach((cp, i) => { const dd = Math.abs(cp - p); if (dd < d) { d = dd; best = i; } });
        return best >= 0 && d < U(28) ? this.chapters[best] : null;
      };
      const U = (u) => u / this.L;
      const list = [];
      // big welcome clouds right after take-off (Atmos cluster at .034/.037)
      list.push({ p: U(10), x: 5.5, y: -4.6, s: 3 }, { p: U(11), x: 10.7, y: 1, s: 2 }, { p: U(15), x: -7, y: -6, s: 3.2, fx: true }, { p: U(16), x: -15, y: 0, s: 2.5 });
      const density = clamp(this.s.cloud_density, 0.3, 2);
      let p = U(22);
      const stop = 0.8;
      let flip = 1;
      while (p < stop) {
        const ch = nearestChapter(p);
        const textSide = ch ? (ch.side === 'left' ? -1 : ch.side === 'right' ? 1 : 0) : 0;
        if (rnd() < 0.62) { // close cloud the plane brushes past
          let side = textSide ? -textSide : (flip *= -1);
          let y = -3 + rnd() * 4.5;
          if (textSide === 0 && ch) y = -2.5 - rnd() * 1.2;
          const tiny = rnd() < 0.12;
          list.push({ p, x: side * (2.2 + rnd() * 2.6), y, s: tiny ? 0.3 + rnd() * 0.15 : 0.9 + rnd() * 0.6, fx: rnd() < 0.4, ry: rnd() < 0.3 });
        } else { // big distant cloud
          const side = rnd() < 0.5 ? -1 : 1;
          list.push({ p, x: side * (7 + rnd() * 8), y: -9 + rnd() * 16, s: 2 + rnd() * 1.5, fx: rnd() < 0.5 });
        }
        p += U((7 + rnd() * 9) / density);
      }
      this.clouds = list.map((c, i) => {
        const mat = new THREE.MeshLambertMaterial({ color: 0xffffff, transparent: true, opacity: 0 });
        const m = new THREE.Mesh(this.cloudGeos[i % 3], mat);
        m.userData.shape = i % 3;
        P.getPointAt(clamp(c.p, 0, 1), m.position);
        P.getPointAt(clamp(c.p - this.ahead.cloudLook, 0, 1), look);
        m.position.x += c.x; m.position.y += c.y;
        look.y = m.position.y; m.lookAt(look);
        if (c.ry) m.rotation.y += Math.PI;
        const sc = c.s * (this.s.cloud_scale || 1);
        m.scale.set(c.fx ? -sc : sc, sc, sc);
        m.visible = false;
        this.scene.add(m);
        return m;
      });
      void tmp;
    }

    buildTexts() {
      const s = this.s;
      this.ppu = this.device === 'desktop' ? 300 : 270;
      this.texts = this.chapters.map((c, i) => {
        const side = c.side === 'left' ? 'right' : c.side === 'right' ? 'left' : 'center';
        // "side" = where the text stands; anchor = which edge sits on the point
        const offX = c.side === 'left' ? -1.6 : c.side === 'right' ? 1.6 : 0;
        const group = new THREE.Group();
        const p = this.chapterP[i];
        this.curve.getPointAt(p, group.position);
        const pre = new THREE.Vector3(); this.curve.getPointAt(Math.max(0, p - this.ahead.pre), pre);
        const look = new THREE.Vector3(); this.curve.getPointAt(Math.max(0, p - this.ahead.textLook), look);
        group.position.add(new THREE.Vector3(offX, offX === 0 ? 2.1 : 1, 0)); pre.add(new THREE.Vector3(offX, 1, 0)); const side0 = Math.sign(offX);
        look.y = group.position.y; group.lookAt(look);
        this.scene.add(group);
        return { c, i, p, group, pre, offX: side0 * 1.35, align: side === 'center' ? 'center' : side === 'right' ? 'right' : 'left', mesh: null };
      });
      void s;
      this.fontsReady = false;
      const fams = [`400 64px ${this.s.font_title}`, `500 64px ${this.s.font_title}`, `400 64px ${this.s.font_body}`, `italic 800 64px ${this.s.font_title}`];
      const done = () => { this.fontsReady = true; this.texts.forEach((t) => this.disposeText(t)); };
      if (document.fonts && document.fonts.load) Promise.all(fams.map((f) => document.fonts.load(f).catch(() => null))).then(done, done);
      else done();
      setTimeout(() => { if (!this.fontsReady) done(); }, 2500);
    }

    makeTextMesh(t) {
      const c = t.c, s = this.s, ppu = this.ppu;
      // Atmos: title .5u wide 2.5u, body .18u wide 3u. Long titles get a
      // wider measure and a slightly smaller size so they stay on 2-3 lines.
      // Narrow screens (fov 90): body text is boosted so it stays readable.
      const boost = this.textBoost || 1, wide = 1;
      const long = (c.title || '').length > 16, xlong = (c.title || '').length > 22;
      const titleSize = (long ? 0.4 : 0.48) * s.title_size * (boost > 1 ? 1 + (boost - 1) * 0.45 : 1), bodySize = 0.17 * s.body_size * boost, supSize = 0.16 * s.body_size * boost;
      const titleMax = (xlong ? 3.15 : long ? 2.9 : 2.5) * s.text_width * wide, bodyMax = (boost > 1 ? 2.75 : 2.9) * s.text_width * wide;
      const cv = document.createElement('canvas'), ctx = cv.getContext('2d');
      const titleFont = `${s.title_weight} ${Math.round(titleSize * ppu)}px ${s.font_title}`;
      const bodyFont = `400 ${Math.round(bodySize * ppu)}px ${s.font_body}`;
      const supFont = `500 ${Math.round(supSize * ppu)}px ${s.font_body}`;
      const title = s.title_uppercase ? (c.title || '').toUpperCase() : c.title || '';
      ctx.font = titleFont; const tLines = title ? balancedWrap(ctx, title, titleMax * ppu) : [];
      let maxW = 0; tLines.forEach((l) => { maxW = Math.max(maxW, ctx.measureText(l).width); });
      ctx.font = bodyFont; const bLines = c.body ? balancedWrap(ctx, c.body, bodyMax * ppu) : [];
      bLines.forEach((l) => { maxW = Math.max(maxW, ctx.measureText(l).width); });
      if (c.eyebrow) { ctx.font = supFont; maxW = Math.max(maxW, ctx.measureText(c.eyebrow).width * 1.08); }
      const pad = 0.15;
      const W = maxW / ppu + pad * 2;
      let h = pad;
      const supH = c.eyebrow ? supSize * 1.3 + 0.12 : 0;
      h += supH + tLines.length * titleSize * 1.05 + (bLines.length ? 0.26 + bLines.length * bodySize * 1.55 : 0) + pad;
      cv.width = Math.ceil(W * ppu); cv.height = Math.ceil(h * ppu);
      const ax = t.align === 'left' ? pad * ppu : t.align === 'right' ? cv.width - pad * ppu : cv.width / 2;
      ctx.textAlign = t.align; ctx.textBaseline = 'top';

      // soft halo tinted with the sky of that moment, stronger on light skies
      const m = this.sampleMood(t.p, { top: [0, 0, 0], bottom: [0, 0, 0], noise: [0, 0, 0], ground: [0, 0, 0], light: [1, 1, 1], stars: 0 });
      // sky behind the text ~ 42% down the vertical gradient
      const bg = mixArr([0, 0, 0], m.top, m.bottom, 0.42);
      const lum = 0.2126 * bg[0] + 0.7152 * bg[1] + 0.0722 * bg[2];
      const ink = s.text_auto_contrast && lum > 0.66;
      let col = s.text_color || '#ffffff', halo = null;
      if (ink) {
        // deep ink tinted by the sky (navy on blue, plum on peach)
        const r = Math.round(lerp(m.top[0] * 90, 22, 0.55)), g = Math.round(lerp(m.top[1] * 70, 28, 0.55)), b = Math.round(lerp(m.top[2] * 120, 92, 0.55));
        col = `rgb(${r},${g},${b})`;
        halo = s.text_shadow > 0 ? `rgba(255,255,255,${(s.text_shadow * 0.55).toFixed(3)})` : null;
      } else if (s.text_shadow > 0) {
        const hr = Math.round(lerp(m.top[0], m.noise[0], 0.5) * 110), hg = Math.round(lerp(m.top[1], m.noise[1], 0.5) * 100), hb = Math.round(lerp(m.top[2], m.noise[2], 0.5) * 150 + 20);
        halo = `rgba(${hr},${hg},${hb},${clamp(s.text_shadow * (0.45 + lum), 0, 0.9).toFixed(3)})`;
      }
      // all lines drawn by one routine at any scale: crisp text on the main
      // canvas, and a quarter-size copy in the halo colour that is scaled up
      // behind it (the upscale itself makes the soft glow; no shadowBlur)
      const paint = (g, k, color) => {
        g.textAlign = t.align; g.textBaseline = 'top'; g.fillStyle = color;
        const X = ax * k; let y = pad * ppu;
        if (c.eyebrow) {
          g.font = supFont.replace(/(\d+)px/, (m0, n) => Math.max(1, Math.round(n * k)) + 'px');
          g.globalAlpha = 0.92; g.fillText(c.eyebrow, X, y * k); g.globalAlpha = 1;
          y += (supSize * 1.3 + 0.12) * ppu;
        }
        g.font = titleFont.replace(/(\d+)px/, (m0, n) => Math.max(1, Math.round(n * k)) + 'px');
        tLines.forEach((l) => { g.fillText(l, X, (y - titleSize * 0.06 * ppu) * k); y += titleSize * 1.05 * ppu; });
        if (bLines.length) {
          y += 0.26 * ppu; g.font = bodyFont.replace(/(\d+)px/, (m0, n) => Math.max(1, Math.round(n * k)) + 'px');
          bLines.forEach((l) => { g.fillText(l, X, y * k); y += bodySize * 1.55 * ppu; });
        }
      };
      paint(ctx, 1, col);
      if (halo) {
        const k = 0.25, hc = document.createElement('canvas');
        hc.width = Math.max(1, Math.ceil(cv.width * k)); hc.height = Math.max(1, Math.ceil(cv.height * k));
        const hg = hc.getContext('2d');
        hg.lineJoin = 'round'; hg.strokeStyle = halo; hg.lineWidth = 2;
        paint(hg, k, halo);
        ctx.save(); ctx.globalCompositeOperation = 'destination-over'; ctx.imageSmoothingEnabled = true;
        if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(hc, 0, 0, cv.width, cv.height);
        ctx.drawImage(hc, -0.02 * ppu, 0.02 * ppu, cv.width, cv.height);
        ctx.restore();
      }
      const tex = new THREE.CanvasTexture(cv);
      tex.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
      tex.minFilter = THREE.LinearMipmapLinearFilter; tex.generateMipmaps = true;
      if (this.renderer.initTexture) this.renderer.initTexture(tex); // GPU upload now, not on first view
      const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, opacity: 0 });
      const geo = new THREE.PlaneGeometry(cv.width / ppu, cv.height / ppu);
      const gw = cv.width / ppu, gh = cv.height / ppu;
      // anchor: text block edge on the group origin, sup line just above it
      const tx = t.align === 'left' ? gw / 2 - pad : t.align === 'right' ? -gw / 2 + pad : 0;
      geo.translate(tx, -gh / 2 + pad + supH + 0.05, 0);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.renderOrder = 5;
      t.group.add(mesh);
      t.mesh = mesh;
    }
    disposeText(t) {
      if (!t.mesh) return;
      t.group.remove(t.mesh); t.mesh.geometry.dispose(); t.mesh.material.map.dispose(); t.mesh.material.dispose(); t.mesh = null;
    }

    buildWind() {
      const n = 200, geo = new THREE.OctahedronGeometry(0.02, 0);
      this.windMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, depthWrite: false });
      this.wind = new THREE.InstancedMesh(geo, this.windMat, n);
      this.wind.frustumCulled = false;
      const m = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler(), sc = new THREE.Vector3(1, 50, 1), p = new THREE.Vector3();
      this.windData = [];
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2, r = 3 + Math.random();
        p.set(Math.cos(a) * r, Math.sin(a) * r, Math.random() * 80 - 40);
        e.set(Math.PI / 2, Math.random() * Math.PI * 2, 0); q.setFromEuler(e);
        m.compose(p, q, sc); this.wind.setMatrixAt(i, m);
        this.windData.push({ p: p.clone(), q: q.clone(), v: 0.2 * (1 + (i % 5)) });
      }
      this.windM = m; this.windScale = sc;
      this.scene.add(this.wind);
    }

    /* ------------------------------------------------------- lifecycle */
    start(withSound) {
      if (!this.state) return;
      // already flying (e.g. started by scrolling): a click still turns the sound on
      if (this.state.started) { if (withSound) this.enableSound(true); return; }
      this.state.started = true;
      this.state.startAt = this.state.t;
      this.root.classList.add('dla--started');
      if (withSound) this.enableSound(true);
      setTimeout(() => this.alive && this.root.classList.add('dla--chrome-in'), 1500);
    }

    // Browsers only allow audio after a tap/click/key, never after a scroll.
    enableSound(fx) {
      const snd = this.sound;
      if (snd.on || !snd.hasMusic() || snd.muted) return false;
      if (!snd.start()) return false;
      this.root.classList.add('dla--playing'); this.root.classList.remove('dla--muted');
      if (this.els.sound) this.els.sound.setAttribute('aria-pressed', 'true');
      if (fx) { snd.click(); snd.whoosh(1); }
      return true;
    }

    replay() {
      const st = this.state; if (!st) return;
      const top = window.scrollY + this.els.track.getBoundingClientRect().top;
      this.lockEnd(false); this._endFree = false;
      window.scrollTo({ top: Math.max(0, top), behavior: 'auto' }); this.sy = this._lastSy = window.scrollY;
      st.am = 0; st.tgt = 0; st.ended = false; st.endT = 0; st.introT = 0; st.cloudAlpha = 0; st.lineAlpha = 0;
      st.startAt = st.t; this.root.classList.remove('dla--ended', 'dla--moving'); this._moving = false;
      this.sound.whoosh(0.8);
    }

    selectBlock(blockId) {
      const i = this.chapters.findIndex((c) => c.id === blockId);
      if (i < 0 || !this.state) return;
      this.start(false);
      const st = this.state;
      st.introT = 1; st.cloudAlpha = 1; st.lineAlpha = 0.5; st.startAt = st.t - 10;
      const s = this.s, rI = (s.intro_length + (i + 0.5) * s.chapter_length) / this.totalVh;
      const trackTop = window.scrollY + this.els.track.getBoundingClientRect().top;
      const total = this.els.track.offsetHeight - this.els.stage.offsetHeight;
      window.scrollTo({ top: trackTop + rI * total, behavior: 'auto' }); this.sy = window.scrollY;
      st.am = st.tgt = Math.max(0, this.chapterP[i] - this.ahead.pre - 3 / this.L);
      st.ended = false; st.endT = 0; this.root.classList.remove('dla--ended');
    }

    resize() {
      if (!this.renderer) return;
      const st = this.els.stage, w = st.clientWidth || window.innerWidth, h = st.clientHeight || window.innerHeight;
      this.trackTop = null; // re-measure scroll geometry
      if (this._w === w && Math.abs((this._h || 0) - h) < 2) return;
      this._w = w; this._h = h;
      this.renderer.setPixelRatio(this.dpr);
      this.renderer.setSize(w, h, false);
      this.post.setSize(w, h, this.dpr);
      const aspect = w / h;
      this.camera.aspect = aspect;
      this.camera.fov = clamp(mapLinear(aspect, 1.6, 0.6, 60, 90), 60, 90);
      this.camera.updateProjectionMatrix();
      this.camOffsetFactor = clamp(mapLinear(aspect, 1.6, 0, 0, 1), 0, 1);
      this.deblurFactor = clamp(mapLinear(aspect, 1.6, 0.6, 0, 1), 0, 1);
      const boost = aspect < 1 ? clamp(mapLinear(aspect, 1, 0.45, 1.2, 1.62), 1.2, 1.62) : 1;
      if (boost !== this.textBoost) { this.textBoost = boost; if (this.texts) this.texts.forEach((tx) => this.disposeText(tx)); }
      this.skyMat.uniforms.uYCompression.value = clamp(mapLinear(aspect, 1.6, 0.6, 0.06, 0.04), 0.04, 0.06);
      this.starMat.uniforms.uSize.value = 2.4 * this.dpr;
      if (this.els.handle && this.els.rail) this.railSpan = this.els.rail.clientHeight - this.els.handle.clientHeight;
      // logo after take-off: a fixed on-screen width (about the Atmos title)
      const head = this.q('.dla-intro__head');
      if (head && head.offsetWidth) {
        const target = w < 640 ? Math.min(w * 0.26, 108) : Math.min(w * 0.12, 160);
        const k = clamp(target / head.offsetWidth, 0.12, 0.8);
        this.root.style.setProperty('--dla-head-scale', k.toFixed(3));
        this.root.style.setProperty('--dla-head-h', Math.round(head.offsetHeight * k) + 'px');
      }
    }

    /* the stage is sticky inside a tall track: a fast fling used to carry the
       page past the track into the store footer while the camera (which lags
       the scroll on purpose) was still flying. Once the visitor scrolls down
       into the finale band the page is held at the end of the track until the
       finale screen is up, then released. */
    guardEnd() {
      const st = this.state, y = this.sy, prev = this._lastSy == null ? y : this._lastSy;
      this._lastSy = y;
      if (!st || !st.started || this.designMode || this._endFree) return;
      if (this.trackTop == null) this.measureTrack();
      const lockY = this.trackTop + this.trackTotal;
      if (!this._endLock && y > prev && y >= this.trackTop + this.rEnd * this.trackTotal) this.lockEnd(true);
      if (this._endLock && y > lockY) { window.scrollTo(0, lockY); this.sy = this._lastSy = lockY; }
    }
    lockEnd(on) {
      if (!!this._endLock === on) return;
      this._endLock = on;
      this._endLockAt = on ? this.state.t : 0;
      document.documentElement.classList.toggle('dla-endlock', on);
      // iOS keeps scrolling under overflow:hidden: block new gestures too
      const block = this._blockScroll || (this._blockScroll = (e) => { if (e.cancelable) e.preventDefault(); });
      const opts = { passive: false };
      if (on) { window.addEventListener('wheel', block, opts); window.addEventListener('touchmove', block, opts); }
      else { window.removeEventListener('wheel', block, opts); window.removeEventListener('touchmove', block, opts); }
    }

    measureTrack() {
      const t = this.els.track;
      this.trackTop = t.getBoundingClientRect().top + window.scrollY;
      this.trackTotal = t.offsetHeight - this.els.stage.offsetHeight;
      this.measuredAt = performance.now();
    }
    scrollRatio() {
      // scrollY comes from the scroll event and geometry is re-measured only
      // when the page resizes: no layout reads inside the animation frame
      if (this.trackTop == null) this.measureTrack();
      if (this.sy == null) this.sy = window.scrollY;
      return this.trackTotal > 0 ? clamp((this.sy - this.trackTop) / this.trackTotal, 0, 1) : 0;
    }

    /* ----------------------------------------------------------- frame */
    loop() {
      if (!this.alive) return;
      this.raf = requestAnimationFrame(this.loop);
      // rAF timestamps are unreliable in some webviews: use our own clock
      const now = performance.now();
      let dt = (now - this.last) / 1000; this.last = now;
      dt = Number.isFinite(dt) ? clamp(dt, 0, 0.1) : 1 / 60;
      if (!this.inView || document.hidden) return;
      const st = this.state, s = this.s;
      st.t += dt;
      const t = st.t;

      // adaptive quality: every 60 frames, if we run under ~48 fps, step down
      // (resolution first, then edge blur). Never steps back up, so no flicker.
      // only steady flight is measured (not the start-up, not one-off spikes);
      // two slow windows in a row are needed; blur goes before resolution
      const pf = this.perf || (this.perf = { n: 0, acc: 0, slow: 0, steps: 0 });
      const warm = st.started && st.t - st.startAt > 4 && (this.cloudsReady || 0) >= 3 && !this._textIdle;
      if (pf.steps < 4 && warm && dt < 0.1) {
        pf.n++; pf.acc += dt;
        if (pf.n === 90) {
          const avg = pf.acc / 90; pf.n = 0; pf.acc = 0;
          pf.slow = avg > 1 / 45 ? pf.slow + 1 : 0;
          if (pf.slow >= 2) {
            pf.slow = 0; pf.steps++;
            if (this.blurOn && this.device !== 'desktop') this.blurOn = false;
            else if (this.dpr > this.minDpr + 0.01) { this.dpr = Math.max(this.minDpr, this.dpr * 0.85); this._w = 0; this.resize(); }
            else pf.steps = 4;
          }
        }
      }

      const r = this.scrollRatio();
      if (!st.started && r > 0.004) this.start(false);
      const needs = st.started && !this.sound.on && !this.sound.muted && this.sound.hasMusic();
      if (needs !== this._needs) { this._needs = needs; this.root.classList.toggle('dla--needs-sound', needs); }
      const pinned = s.hide_header && r > 0.0005 && r < 0.9995 && !this.designMode;
      if (pinned !== this._pinned) { this._pinned = pinned; document.documentElement.classList.toggle('dla-hide-header', pinned); }

      // intro timeline (Atmos: plane flies in 3s power2.out, clouds + line fade after .5s)
      if (st.started) {
        const since = t - st.startAt;
        st.introT = Math.max(st.introT, easeOut2(clamp(since / 3, 0, 1)));
        st.cloudAlpha = Math.max(st.cloudAlpha, easeIn2(clamp((since - 0.5) / 2, 0, 1)));
        st.lineAlpha = Math.max(st.lineAlpha, 0.5 * clamp((since - 0.5) / 2, 0, 1));
      }

      // target progress: scroll lerp .2 (Atmos virtual scroll smoothing)
      // chapters sit in the middle of their scroll band; past the last band
      // the target jumps to 1 so the camera crosses .95 and the finale plays
      const pT = !st.started ? 0 : r < this.rEnd ? 0.95 * r / this.rEnd : 1;
      st.tgt += (pT - st.tgt) * frameLerp(0.2, dt);

      // brake near the closest chapter so it can be read
      const cam = this.camera;
      let closest = null, cd = Infinity;
      for (const tx of this.texts) { const d = Math.abs(st.am - tx.p); if (d < cd) { cd = d; closest = tx; } }
      let e = 0;
      if (closest) { e = clamp(1 - cam.position.distanceTo(closest.pre) / this.holdRadius, 0, 1); e = Math.pow(e, 3); }
      const amp = Math.max(1 - 1.2 * e * s.read_pause, this._endLock ? 0.3 : 0.01);
      const prevAm = st.am;
      if (!st.ended) st.am += (st.tgt - st.am) * frameLerp(0.025 * amp * s.camera_speed, dt);
      st.am = clamp(st.am, 0, 0.999);
      const speed = Math.abs(st.am - prevAm) * this.L / Math.max(dt, 1e-3); // world units / s

      // ending / un-ending
      if (!st.ended && st.am >= 0.95) { st.ended = true; st.endedAt = t; st.endFrom = st.endT; this.root.classList.add('dla--ended'); }
      else if (st.ended && st.tgt < 0.9) { st.ended = false; this._endFree = false; this.root.classList.remove('dla--ended'); }
      if (this._endLock && ((st.ended && t - st.endedAt > 2.8) || t - this._endLockAt > 14)) { this.lockEnd(false); this._endFree = true; }
      st.endT = st.ended ? Math.min(1, st.endT + dt / 3.5) : Math.max(0, st.endT - dt / 1.2);

      const am = st.am, P = this.curve;
      const camPos = this._v1 || (this._v1 = new THREE.Vector3());
      const tan = this._v2 || (this._v2 = new THREE.Vector3());
      const look = this._v3 || (this._v3 = new THREE.Vector3());
      P.getPointAt(am, camPos);
      P.getTangentAt(am, tan);
      P.getPointAt(Math.min(am + this.ahead.wind, 1), this.wind.position);
      P.getPointAt(Math.min(am + this.ahead.plane, 1), this.planeBase);
      P.getPointAt(Math.min(am + this.ahead.look, 1), this.planeLook);
      cam.position.copy(camPos);
      look.copy(camPos).add(tan);
      cam.lookAt(look);
      this.wind.lookAt(look);

      // camera slides towards the chapter on narrow screens (Atmos camera_offset)
      let mixer = 1;
      if (closest) {
        mixer = 1 - (1 - amp) * Math.max(this.deblurFactor, 0.8);
        const off = e * closest.offX * 3;
        const side = this._v4 || (this._v4 = new THREE.Vector3());
        side.set(1, 0, 0).applyQuaternion(cam.quaternion).multiplyScalar(off * this.camOffsetFactor);
        cam.position.add(side);
        cam.rotation.y += 0.1 * -off * this.camOffsetFactor;
      }
      // finale: camera rises and tilts up
      if (st.endT > 0) {
        const k = easeInOut2(st.endT);
        cam.position.y += 5 * k; cam.rotation.x += 0.2 * k;
      }
      cam.updateMatrixWorld();

      // plane: bob, sway, bank into turns (Atmos), intro fly-in, finale exit
      const pl = this.plane, io = 1 - st.introT;
      pl.position.set(
        this.planeBase.x + 0.1 * (-0.5 + Math.sin(0.5 * t)) + this.introOffset.x * io,
        this.planeBase.y + 0.2 * Math.sin(t) + this.introOffset.y * io,
        this.planeBase.z + this.introOffset.z * io
      );
      pl.lookAt(this.planeLook);
      pl.rotation.z += clamp(0.15 * (this.planeLook.x - pl.position.x), -0.55, 0.55);
      let planeOpacity = 1;
      if (st.endT > 0) {
        const k = easeIn2(clamp(st.endT * 3.5 / 2, 0, 1));
        const fwd = this._v5 || (this._v5 = new THREE.Vector3());
        fwd.copy(tan).multiplyScalar(20 * k); pl.position.add(fwd); pl.position.y += 5 * k; pl.rotation.x -= 0.3 * k;
        planeOpacity = 1 - k;
      }
      this.planeMats.forEach((m) => { m.opacity = planeOpacity; });
      pl.visible = planeOpacity > 0.01;
      this.lineMat.opacity = st.lineAlpha * planeOpacity;

      // sky + light follow the journey
      this.sky.position.copy(cam.position);
      this.stars.position.copy(cam.position);
      this.skyMat.uniforms.uTime.value = t;
      this.starMat.uniforms.uTime.value = t;
      this.updateMood(am);

      // clouds fade with distance: 2 * (1 - d/30)^2
      for (const c of this.clouds) {
        let o = clamp(1 - c.position.distanceTo(cam.position) / 30, 0, 1);
        o = Math.min(1, 2 * o * o) * st.cloudAlpha;
        c.material.opacity = o; c.visible = o > 0.005;
      }

      // chapter texts: lazy textures, same distance fade
      for (const tx of this.texts) {
        const d = tx.group.position.distanceTo(cam.position);
        if (this.fontsReady && !tx.mesh && d < 95 && !this._textIdle) {
          // prepare upcoming chapters in idle time, well before they are visible
          this._textIdle = true;
          (window.requestIdleCallback || ((cb) => setTimeout(cb, 30)))(() => { this._textIdle = false; if (this.alive && !tx.mesh) this.makeTextMesh(tx); }, { timeout: 600 });
        }
        if (d < 40 && this.fontsReady && !tx.mesh) this.makeTextMesh(tx);
        if (d > 130 && tx.mesh) this.disposeText(tx); // well beyond the prepare distance (95): no build/dispose loop
        if (tx.mesh) {
          let o = clamp(1 - d / 30, 0, 1); o = Math.min(1, 2 * o * o) * st.cloudAlpha * (1 - st.endT);
          tx.mesh.material.opacity = o; tx.mesh.visible = o > 0.005;
        }
      }

      // wind streaks: speed driven
      const wd = this.windData, m = this.windM;
      for (let i = 0; i < wd.length; i++) {
        const w = wd[i]; w.p.z = ((w.p.z + w.v * dt * 60 + 40) % 80) - 40;
        m.compose(w.p, w.q, this.windScale); this.wind.setMatrixAt(i, m);
      }
      this.wind.instanceMatrix.needsUpdate = true;
      const windTarget = st.started && !st.ended ? clamp((speed - 4) * 0.012, 0, 0.1) : 0;
      this.windMat.opacity = lerp(this.windMat.opacity, windTarget, frameLerp(0.05, dt));
      this.wind.visible = this.windMat.opacity > 0.002;

      // UI: hint, rail, counter
      if (st.started && am > 0.003 && !this._moving) { this._moving = true; this.root.classList.add('dla--moving'); }
      const hy = Math.round(r * (this.railSpan || 0));
      if (this.els.handle && hy !== this._hy) { this._hy = hy; this.els.handle.style.transform = `translate3d(0,${hy}px,0)`; }
      if (this.els.counter && this.N) {
        let idx = 0; this.chapterP.forEach((cp, i) => { if (am >= cp - this.ahead.pre * 2) idx = i; });
        const label = String(idx + 1).padStart(2, '0') + ' / ' + String(this.N).padStart(2, '0');
        if (label !== this._counter) { this._counter = label; this.els.counter.textContent = label; }
      }

      this.sound.update(clamp((speed - 1) / 14, 0, 1), this.brightness, { started: st.started, ended: st.ended, night: this.cur.stars });

      this.post.render(this.scene, cam, mixer, t, this.blurOn);
    }

    destroy() {
      this.alive = false;
      cancelAnimationFrame(this.raf);
      document.documentElement.classList.remove('dla-hide-header');
      if (this._endLock) this.lockEnd(false);
      this.listeners.forEach(([tg, ty, fn, o]) => tg.removeEventListener(ty, fn, o));
      if (this.cloudWorker) this.cloudWorker.terminate();
      if (this.ro) this.ro.disconnect();
      if (this.bodyRo) this.bodyRo.disconnect();
      if (this.io) this.io.disconnect();
      if (this.io2) this.io2.disconnect();
      document.documentElement.classList.remove('dla-focus');
      if (this.sound) this.sound.destroy();
      if (this.scene) {
        this.scene.traverse((o) => {
          if (o.geometry) o.geometry.dispose();
          if (o.material) { (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => { if (m.map) m.map.dispose(); m.dispose(); }); }
        });
      }
      if (this.post) this.post.dispose();
      if (this.renderer) { this.renderer.dispose(); if (this.renderer.forceContextLoss) this.renderer.forceContextLoss(); }
    }
  }

  /* --------------------------------------------------------------- boot */
  const instances = new Map();
  function bootAll(scope) {
    if (!window.THREE) return;
    (scope || document).querySelectorAll('[data-dla]').forEach((el) => {
      if (instances.has(el)) return;
      instances.set(el, new Dreamland(el));
    });
  }
  function killIn(scope) {
    scope.querySelectorAll('[data-dla]').forEach((el) => { const i = instances.get(el); if (i) i.destroy(); instances.delete(el); });
  }
  window.DLA = { bootAll, instances, Sound };
  document.addEventListener('shopify:section:load', (e) => bootAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => killIn(e.target));
  document.addEventListener('shopify:block:select', (e) => { instances.forEach((inst) => inst.selectBlock && inst.selectBlock(e.detail.blockId)); });

  function whenThree(cb, tries) {
    if (window.THREE) return cb();
    if ((tries || 0) > 200) { document.querySelectorAll('[data-dla]').forEach((el) => el.classList.add('dla--nogl')); return; }
    setTimeout(() => whenThree(cb, (tries || 0) + 1), 50);
  }
  const go = () => whenThree(() => bootAll(document));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go); else go();
})();
